/* Dedup review-cluster contract adapter (marker: dedup-cluster-adapter-v1)
 *
 * WHY THIS EXISTS — a live, pre-existing contract mismatch between the worker
 * (`GET /api/crm/dedup/review-clusters`, api/src/index.ts) and the FE cluster
 * merge UI (src/views/preferences/admin/Duplicates.tsx). The worker and the FE
 * disagree on nearly every field name AND the tier enum, and there was NO
 * transform in `fetchDedupReviewClusters` — it returned `resp.json()` raw. The
 * live consequences were:
 *   - `TIER_META[cluster.tier]` for a `multi_billed` cluster → undefined →
 *     `meta.label` threw → RENDER CRASH for every multi-billed cluster.
 *   - `cluster.cluster_id` undefined → React key collisions + survivor keyed
 *     by "undefined".
 *   - `survivor_id`/`member.id` undefined → survivor radio never checked;
 *     `losers` filtered to [] → the Merge button was permanently disabled.
 *   - `member.name` undefined → "(unnamed)"; invoices/total rendered NaN/$0.
 *   - Dismiss sent `{cluster_id}` but the worker requires `{normalized_name}`
 *     → 400 "normalized_name is required".
 *
 * This module is the single, pure, tested translation from the worker's raw
 * shape to the FE-facing shape. It performs the tier rename, the field renames,
 * the string→number `data_quality` coercion, and the `is_survivor` derivation.
 * Keep it pure (no fetch, no React) so vitest can pin the contract.
 *
 * Field / enum map (worker → FE):
 *   cluster.normalized_name            → cluster.cluster_id  (+ kept as normalized_name)
 *   cluster.proposed_survivor_id       → cluster.survivor_id
 *   cluster.tier 'multi_billed'        → 'multiple_billed'   (all_zero / one_billed pass through)
 *   member.unified_client_id           → member.id (+ is_survivor = id === proposed_survivor_id)
 *   member.preferred_name              → member.name
 *   member.inv_count                   → member.invoices
 *   member.legacy_total_invoiced       → member.total_invoiced
 *   member.data_quality (string|null)  → member.data_quality (number|null)
 */

export const DEDUP_CLUSTER_ADAPTER_MARKER = 'dedup-cluster-adapter-v1'

// ---------------------------------------------------------------------------
// FE-facing shape (source of truth — src/api.ts re-exports these).
// ---------------------------------------------------------------------------
export type DedupTier = 'multiple_billed' | 'all_zero' | 'one_billed'

export type DedupMember = {
  id: string
  name: string
  invoices: number
  total_invoiced: number
  balance: number
  address: string | null
  data_quality: number | null
  is_survivor: boolean
}

export type DedupCluster = {
  cluster_id: string
  normalized_name: string
  tier: DedupTier
  survivor_id: string
  members: DedupMember[]
}

export type DedupReviewResponse = {
  clusters: DedupCluster[]
  total: number
  page: number
  pageSize: number
}

// ---------------------------------------------------------------------------
// Raw worker shape (api/src/index.ts GET /api/crm/dedup/review-clusters).
// ---------------------------------------------------------------------------
export type RawDedupMember = {
  unified_client_id: string
  preferred_name: string | null
  client_type?: string | null
  inv_count: number
  legacy_total_invoiced: number
  balance: number
  deal_count?: number
  lead_count?: number
  activity_count?: number
  has_data?: boolean
  phone?: string | null
  email?: string | null
  address: string | null
  data_quality: string | null
}

export type RawDedupCluster = {
  normalized_name: string
  member_count?: number
  members: RawDedupMember[]
  proposed_survivor_id: string
  tier: string
}

export type RawDedupReviewResponse = {
  clusters?: RawDedupCluster[]
  total?: number
  page?: number
  pageSize?: number
  tier?: string | null
  entity?: string
  tierCounts?: Record<string, number>
}

// ---------------------------------------------------------------------------
// tier rename map. The worker emits `multi_billed`; the FE enum is
// `multiple_billed`. `all_zero` / `one_billed` are shared. An unrecognized
// tier is passed through unchanged so the FE can render a fallback badge
// instead of crashing (Duplicates.tsx resolves tier meta defensively).
// ---------------------------------------------------------------------------
export const TIER_RENAME: Record<string, DedupTier> = {
  multi_billed: 'multiple_billed',
  multiple_billed: 'multiple_billed',
  all_zero: 'all_zero',
  one_billed: 'one_billed',
}

export function mapTier(raw: string | null | undefined): DedupTier {
  const key = String(raw ?? '').toLowerCase().trim()
  // Known tier → canonical FE value; unknown → pass the raw string through
  // (cast) so the FE fallback badge renders. Never throw here.
  return TIER_RENAME[key] ?? (key as DedupTier)
}

// ---------------------------------------------------------------------------
// data_quality is a categorical string on the worker (`verified`, `review`,
// null, and rarely `junk`/`duplicate`), but the FE quality chip expects a
// 0–100 numeric score (or null → "—"). Map the known labels to representative
// scores (verified > review, matching the worker's survivor-rank semantics),
// parse a numeric string if one is ever stored, and leave anything unknown as
// null so it renders the neutral "—" chip rather than a fabricated red score.
// ---------------------------------------------------------------------------
const DATA_QUALITY_SCORE: Record<string, number> = {
  verified: 90,
  review: 60,
  junk: 10,
  duplicate: 10,
}

export function dataQualityToScore(raw: unknown): number | null {
  if (raw == null) return null
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  const s = String(raw).trim()
  if (!s) return null
  // Numeric string like "85" → the number itself.
  if (/^\d+(?:\.\d+)?$/.test(s)) {
    const n = Number(s)
    return Number.isFinite(n) ? n : null
  }
  const key = s.toLowerCase()
  return key in DATA_QUALITY_SCORE ? DATA_QUALITY_SCORE[key] : null
}

// ---------------------------------------------------------------------------
// Member + cluster mappers.
// ---------------------------------------------------------------------------
export function mapWorkerMember(raw: RawDedupMember, proposedSurvivorId: string): DedupMember {
  return {
    id: raw.unified_client_id,
    name: raw.preferred_name ?? '',
    invoices: Number(raw.inv_count ?? 0),
    total_invoiced: Number(raw.legacy_total_invoiced ?? 0),
    balance: Number(raw.balance ?? 0),
    address: raw.address ?? null,
    data_quality: dataQualityToScore(raw.data_quality),
    is_survivor: raw.unified_client_id === proposedSurvivorId,
  }
}

export function mapWorkerCluster(raw: RawDedupCluster): DedupCluster {
  const survivorId = raw.proposed_survivor_id
  return {
    cluster_id: raw.normalized_name,
    normalized_name: raw.normalized_name,
    tier: mapTier(raw.tier),
    survivor_id: survivorId,
    members: (raw.members || []).map((m) => mapWorkerMember(m, survivorId)),
  }
}

export function mapWorkerDedupResponse(raw: RawDedupReviewResponse): DedupReviewResponse {
  return {
    clusters: (raw.clusters || []).map(mapWorkerCluster),
    total: Number(raw.total ?? 0),
    page: Number(raw.page ?? 1),
    pageSize: Number(raw.pageSize ?? 0),
  }
}
